"use client";

import React, { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useRouter } from "next/navigation";
import Image from "next/image";

import InputHolder from "./inputHolder";
import MultiselectInputHolder from "./multiselectInputHolder";
import Textarea from "./textarea";
import SelectInputHolder from "./selectInputHolder";

import { validate } from "@/utils/validation";
import { postRequest } from "@/utils/postData";
import { getStrapiURL } from "@/utils/api-helpers";
import { googleRecaptcha } from "@/utils/googleRecaptcha";
import { getLeadTrackingData } from "@/utils/leadTracking";
import {
  getThankYouUrl,
  pushEnhancedConversionsData,
} from "@/utils/gtmEnhancedConversions";

import NotifiactionIcon from "@/app/assets/notification.svg";
import { ValidationErrors } from "../../../global";

export default function HomepageForm({
  formData,
  inOverlay = false,
}: {
  formData?: any;
  inOverlay?: boolean;
}) {
  const router = useRouter();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitError, setFormSubmitError] = useState(false);
  const [formInputs, setFormInputs] = useState({
    name: "",
    mobileNumber: "",
    company: "",
    email: "",
    description: "",
    howDidYouHearAboutUs: "",
  });
  const [allInputsNotEmpty, setAllInputsNotEmpty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    nameError: null,
    mobileNumberError: null,
    descriptionError: null,
    companyError: null,
    emailError: null,
    howDidYouHearAboutUsError: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleInputChange = async (name: any, value: any) => {
    if (name === "howDidYouHearAboutUsOther") {
      setFormInputs((prev: any) => ({
        ...prev,
        howDidYouHearAboutUs: `Others - ${value}`,
      }));
    } else {
      setFormInputs((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const renderErrorMessage = (errorName: string) => {
    const error = validationErrors[errorName + "Error"];
    if (error)
      return <div className="mt-1 pl-2 text-red text-sm">{error}</div>;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // validate input
    const errors = validate(formInputs);
    setValidationErrors(errors);
    // Check if there are any errors
    if (Object.values(errors).some((error) => error !== null)) {
      setIsSubmitting(false);
      setFormSubmitError(true);
      setTimeout(() => {
        setFormSubmitError(false);
      }, 1500);
      return;
    }

    const recaptchaSuccess = await googleRecaptcha(executeRecaptcha);
    // if recaptcha success, submit form to backend
    if (recaptchaSuccess) {
      const leadTracking = getLeadTrackingData();
      let url = `${getStrapiURL(`/api/homepage-form-submission-datas`)}`; // api url based on selected subject
      let res = await postRequest(url, {
        formInputs,
        leadTracking,
        token: recaptchaSuccess, // add newToken into form POST request to do backend recaptcha verification
      });


      if (res.status === 400) {
        setFormSubmitError(true);
        setTimeout(() => {
          setFormSubmitError(false);
        }, 1500);
        setIsSubmitting(false);
      } else {
        const currentPath = window.location.pathname;
        const source =
          currentPath === "/" ? "homepage" : currentPath.slice(1);

        const conversionMeta = {
          formType: "homepage_form",
          leadType: "business" as const,
          source,
          trafficSource: leadTracking.trafficSource,
        };

        // Push enhanced conversions data to GTM
        pushEnhancedConversionsData(formInputs, conversionMeta);

        setFormInputs({
          name: "",
          mobileNumber: "",
          company: "",
          email: "",
          description: "",
          howDidYouHearAboutUs: "",
        });

        setTimeout(() => {
          setIsSubmitting(false);
          setFormSubmitted(true);
          router.push(getThankYouUrl(conversionMeta));
        }, 2000);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  // the code below make sure all fields are not empty
  useEffect(() => {
    let allNotEmpty = true; // Flag to track if all values are not empty

    Object.values(formInputs).forEach((value) => {
      if (value === "") {
        allNotEmpty = false;
        return;
      }
    });
    setAllInputsNotEmpty(allNotEmpty);
  }, [formInputs]);


  return (
    <div
      id={!inOverlay ? "get-in-touch" : undefined}
      className={`${formData
        ? "overflow-x-clip z-10 relative"
        : ""
        } ${inOverlay ? "bg-transparent pt-0" : "bg-darkPurple pt-16"}`}
    >
      {formData && !inOverlay && <h2 className=" text-center">{formData.heading}</h2>}
      <div
        className={`kd-form relative px-8 ${inOverlay ? "pb-8 lg:px-0" : "pb-24 sm:pb-[7vh] lg:px-[17.188vw]"
          }`}
      >
        <form
          className={`relative grid grid-cols-1 md:grid-cols-2 gap-x-6 ${inOverlay ? "gap-y-4" : "gap-y-6"
            }`}
        >
          {formData &&
            formData.form.input.map(
              (inputs: any, index: number) => {
                const gridPlacement =
                  inputs.name === "name"
                    ? "md:col-start-1 md:row-start-1"
                    : inputs.name === "company"
                      ? "md:col-start-1 md:row-start-2"
                      : inputs.name === "email"
                        ? "md:col-start-1 md:row-start-3"
                        : inputs.name === "mobileNumber"
                          ? "md:col-start-2 md:row-start-1"
                          : inputs.inputType === "description"
                            ? "md:col-start-2 md:row-start-2 md:row-span-2"
                            : "";

                if (inputs.inputType === "text") {
                  return (
                    <div
                      className={`relative w-full ${gridPlacement}`}
                      key={index}
                    >
                      <InputHolder
                        input={inputs}
                        onUpdate={handleInputChange}
                      />
                      <div className="text-red font-bold absolute right-4">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (inputs.inputType === "multiselect") {
                  return (
                    <div className={`relative ${gridPlacement}`} key={index}>
                      <MultiselectInputHolder
                        input={inputs}
                        onUpdateService={
                          handleInputChange
                        }
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (inputs.inputType === "description") {
                  return (
                    <div
                      className={`relative w-full ${gridPlacement}`}
                      key={index}
                    >
                      <Textarea
                        input={inputs}
                        onUpdate={handleInputChange}
                        className="h-full"
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (
                  inputs.inputType === "select" &&
                  inputs.name == "howDidYouHearAboutUs"
                ) {
                  return (
                    <React.Fragment key={index}>
                      <div className={`relative w-full ${gridPlacement}`}>
                        <SelectInputHolder
                          input={inputs}
                          onUpdate={handleInputChange}
                        />
                        <div className="text-red font-bold absolute right-4 z-0">
                          {(!formInputs.howDidYouHearAboutUs?.startsWith(
                            "Others"
                          ) ||
                            formInputs.howDidYouHearAboutUs?.startsWith(
                              "Others - "
                            )) &&
                            renderErrorMessage(
                              inputs.name
                            )}
                        </div>
                        {(formInputs.howDidYouHearAboutUs ===
                          "Others" ||
                          formInputs.howDidYouHearAboutUs?.startsWith(
                            "Others - "
                          )) && (
                            <div className="relative w-full pt-5">
                              <InputHolder
                                input={{
                                  name: "howDidYouHearAboutUsOther",
                                  placeholder:
                                    "Please specify...",
                                  label: "Please specify",
                                }}
                                onUpdate={
                                  handleInputChange
                                }
                              />
                              <div className="text-red font-bold absolute right-4">
                                {renderErrorMessage(
                                  inputs.name
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </React.Fragment>
                  );
                } else if (inputs.inputType === "select") {
                  return (
                    <div className={`relative ${gridPlacement}`} key={index}>
                      <SelectInputHolder
                        input={inputs}
                        onUpdate={handleInputChange}
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                }
              }
            )}
        </form>

        <div className={`flex flex-col items-center w-full ${inOverlay ? "mt-3" : "mt-5"}`}>
          <span className="text-white text-sm md:text-base">
            By submitting, you agree to our{" "}
            <a
              className="text-cyan underline"
              href="/privacy"
              target="_blank"
            >
              Privacy Policy
            </a>
          </span>
        </div>

        {formData && (
          <div
            className={`submit-holder flex flex-col w-full ${inOverlay
              ? "items-center mt-5"
              : "md:items-end md:justify-end mt-12 md:px-4"
              }`}
          >
            <div
              className={`submit-button ${isSubmitting
                ? "cursor-not-allowed pointer-events-none"
                : "cursor-pointer"
                }`}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <span
                className={`text-font-24 w-fit font-semibold bg-green px-9 py-4 rounded-full text-darkGrey leading-[1] flex`}
              >
                Submit
              </span>
            </div>
            <span className="text-white mt-2">
              {!inOverlay && (
                <>
                  Start a new project with us{" "}
                  <a
                    className="text-cyan underline"
                    href="/get-in-touch"
                  >
                    now.
                  </a>
                </>
              )}
            </span>
          </div>
        )}

        <div
          className={`absolute bottom-0 left-0 right-0 z-10 flex my-4 m-auto w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-4 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 pointer-events-none ${formSubmitted
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-5"
            } duration-300`}
        >
          <Image
            src={NotifiactionIcon}
            alt={""}
            width={26}
            height={26}
          />
          <span className="text-white text-font-18">
            Thank you for your enquiry! We&apos;ll respond shortly.
          </span>
        </div>

        <div
          className={`absolute left-0 bottom-0 right-0 m-auto flex my-4 w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-3 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 ${formSubmitError
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-7"
            } duration-300 z-20 pointer-events-none shadow-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]`}
        >
          <span className="text-sm text-white tracking-wider">
            Please complete all fields accurately.
          </span>
        </div>

        <div
          className={`absolute left-0 bottom-0 right-0 m-auto flex my-4 w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-3 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 ${isSubmitting
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-7"
            } duration-300 z-20 pointer-events-none shadow-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]`}
        >
          <span className="text-sm text-white tracking-wider">
            Sending your details now. Thanks for waiting!
          </span>
        </div>
      </div>
    </div>
  );
}
